import {ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {BehaviorSubject, filter, map, Observable, Subject, switchMap, take, takeUntil} from 'rxjs';
import {dispatchDeletedItemEvent, dispatchSavedItemEvent, SearchCriteria, SearchResult} from '@snelstart/domain-logic';
import {SharedUiTableModule, TableConfiguration, ZeroStateConfiguration} from '@snelstart/ui-table';
import {
  AllowanceEntity,
  BokkzAlgDataService,
  BokkzClient,
  BokkzTslDataService, BokkzTslLogic,
  ClientEntity,
  FeatureServiceComponent,
  MenuInterface,
  ToeslagenBerekening,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';

@Component({
  selector: 'lib-snelstart-venture-fiscaal-feature-tax-allowance-list',
  templateUrl: './feature-tax-allowance-list.component.html',
  styleUrls: ['./feature-tax-allowance-list.component.scss'],
  imports: [CommonModule, SharedUiTableModule, SharedUiButtonModule, FeatureServiceComponent],
})
export class FeatureTaxAllowanceListComponent implements OnInit, OnDestroy {

  @Output() public details = new EventEmitter<string>();
  protected id: any;
  private destroy$ = new Subject<void>();
  protected tableConfig!: TableConfiguration<any, any>;
  protected clientId: any;
  public dialogVisible = false;
  public showTable = false;
  public year = {} as number;
  public options$ = new BehaviorSubject<MenuInterface[]>([]);
  public options = [] as MenuInterface[];
  public allowanceEntity = <AllowanceEntity>{};
  public client = <ClientEntity>{};
  public tslBerekening = <ToeslagenBerekening>{};
  public hasPartner = false;
  public hasKinderen = false;
  public sumToeslagen = 0;
  public sumVoorschot = 0;
  public rechtOpZorg = 0;
  public rechtOpKgb = 0;
  public rechtOpKot = 0;
  public rechtOpHuur = 0;
  public sumSaldo = 0;
  public activePage = '';

  constructor(
    private router: Router,
    public dataService: BokkzTslDataService,
    public algDataService: BokkzAlgDataService,
    public bokkzTslLogic: BokkzTslLogic,
    public fb: FormBuilder,
    private bokkzClient: BokkzClient,
    private cd: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.id = params['allowanceId'];
      this.clientId = params['clientId'];
    });
    this.algDataService.selectedYear.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.year = value;
      this.showTable = false;
      setTimeout(() => {
        this.createTableConfig();
        this.showTable = true;
      }, 0);
    });
    this.dataService.allowanceEntity.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.allowanceEntity = value;
    });
    this.dataService.hasPartner.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.hasPartner = value;
    });
    this.dataService.hasKinderen.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.hasKinderen = value;
    });
    this.algDataService.client.pipe(takeUntil(this.destroy$)).subscribe((client) => {
      this.client = client;
    });
    this.dataService.tslBerekening.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.tslBerekening = value;
      this.rechtOpHuur = value.rechtOpHuurtoeslag || 0;
      this.rechtOpZorg = value.rechtOpZorgtoeslag || 0;
      this.rechtOpKgb = value.rechtOpKGB || 0;
      this.rechtOpKot = value.rechtOpKOT || 0;
      this.getSumToeslagen();
    });
    this.dataService.options.pipe(takeUntil(this.destroy$)).subscribe((menuInterface) => {
      const old = this.options$.value;
      if (JSON.stringify(this.options$.value) != JSON.stringify(menuInterface)) {
        this.options$.next([...menuInterface.reverse()]);
        this.cd.detectChanges();
        for (let i = 0; i < menuInterface.length; i++) {
          if (JSON.stringify(old[i]) != JSON.stringify(menuInterface[i])) {
            dispatchSavedItemEvent({
              identifier: menuInterface[i].value,
              result: menuInterface[i] as unknown as Record<number, MenuInterface>,
            });
            if (!menuInterface[i].visible) {
              dispatchDeletedItemEvent({
                identifier: menuInterface[i].value,
              });
            }
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createTableConfig(): void {
    const criteria = new SearchCriteria();

    this.tableConfig = TableConfiguration.create({
      identifier: 'value',
      options: {
        useSearch: false,
        useSort: true,
      },
      criteria: criteria,

      search: () => {
        return this.bokkzClient.getAllowance(this.clientId, String(this.year)).pipe(
          switchMap((allowanceEntity) => {
            if (allowanceEntity.toeslagen) {
              this.dataService.updateAllowanceTaxEntity(allowanceEntity);
              this.bokkzTslLogic.getBerekening();
            } else {
              this.options$.next([]);
              this.dataService.updateAantalValidaties(0);
            }
            return this.options$.pipe(
              filter((options) => Array.isArray(options)),
              map((options) => {
                const sortedGroups = options
                  .filter((item) => item.visible)
                  .map((group) => ({
                    ...group,
                    subonderdelen: (group.subonderdelen || [])
                      .filter((sub) => sub.visible)
                      .sort((a, b) => a.value - b.value),
                  }))
                  .sort((a, b) => a.value - b.value); // Sorteer hoofdgroepen ook
                return new SearchResult(sortedGroups, sortedGroups.length);
              }),
              take(1),
            );
          }),
        );
      },

      details: (entity) => {
        if (entity) {
          this.details.next(entity.label);
        }
      },

      fromDetailsMap: (entity) => {
        return entity as unknown as Record<number, MenuInterface>;
      },

      zeroStateConfig: ZeroStateConfiguration.create({
        icon: 'book-open-cover',
        mainText: this.year < 2024
          ? 'Module niet beschikbaar'
          : $localize`Geen toeslagen gevonden voor ` + this.year,
        subText: this.year < 2024
          ? 'De module toeslagen wordt door SnelStart ondersteund vanaf fiscaal jaar 2024.'
          : 'Maak een nieuwe toeslagenberekening aan en start direct met invullen.',
        buttonText: this.year < 2024 ? undefined : `Maak nieuwe toeslagen berekening`,
        buttonConfig: this.year < 2024 ? undefined  : ButtonConfiguration.create({
          onClick: () => {
            return new Observable((subscriber) => {
              setTimeout(() => {
                subscriber.next((this.dialogVisible = true));
                subscriber.complete();
              }, 50);
            });
          },
        }),
      }),
    });
  }

  public navigateToSubPage(pageMapping: string): void {
    const url = '/tax-allowance/' + 'modules/' + this.clientId + '/' + pageMapping;
    this.router.navigate([url]).then(() => console.log('complete'));
    this.activePage = pageMapping;
  }

  public getSumToeslagen() {
    const kot1 = this.tslBerekening?.verschuldigdeKOT || 0;
    const kgb1 = this.tslBerekening?.verschuldigdeKGB || 0;
    const huur1 = this.tslBerekening?.verschuldigdeHuurtoeslag || 0;
    const zorg1 = this.tslBerekening?.verschuldigdeZorgtoeslag || 0;
    this.sumSaldo = kot1 + kgb1 + huur1 + zorg1;

    const kot2 = this.tslBerekening?.alOntvangenKOT || 0;
    const kgb2 = this.tslBerekening?.alOntvangenKGB || 0;
    const huur2 = this.tslBerekening?.alOntvangenHuurtoeslag || 0;
    const zorg2 = this.tslBerekening?.alOntvangenZorgtoeslag || 0;
    this.sumVoorschot = kot2 + kgb2 + huur2 + zorg2;

    const kot3 = this.tslBerekening?.rechtOpKOT || 0;
    const kgb3 = this.tslBerekening?.rechtOpKGB || 0;
    const huur3 = this.tslBerekening?.rechtOpHuurtoeslag || 0;
    const zorg3 = this.tslBerekening?.rechtOpZorgtoeslag || 0;
    this.sumToeslagen = kot3 + kgb3 + huur3 + zorg3;
  }

}
